// OPS-24 — the thank-you page's score poll.
//
// Server-side grading normally lands 3–5 s after /submit, but it is a queue: a
// machine restart mid-grade (the 10-16 prod proof) leaves the attempt to the
// grading sweep, which finished ~2.5 min later. Until then the poll stopped
// twice over — a hard 15 s window, and a `refetchInterval` that returned
// `false` the moment a tick had no data, so the one fetch that failed while
// the restarted machine was unhealthy ended the poll for good. The chips kept
// their spinners and only a manual reload showed the scores.
//
// Rules here:
//   • a failed tick is just a tick — the next one is scheduled from the clock,
//     never from the last response (`retry: false`: the interval IS the retry);
//   • backoff by elapsed time, not by attempt count, so a reload or a long
//     outage cannot speed it up;
//   • a hard upper bound (15 min) after which polling stops and the UI says
//     so instead of spinning forever;
//   • a tab coming back from the background refetches once at once — the
//     interval skips ticks while hidden (`refetchIntervalInBackground: false`),
//     so without this a candidate who tabbed away waits a whole slow tick.
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { fetchCandidateResult, type CandidateResult } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";

export const RESULT_POLL = {
  /** Every 1.5 s while grading usually lands. */
  FAST_MS: 1_500,
  FAST_UNTIL_MS: 30_000,
  /** Then every 5 s — the sweep's territory. */
  MEDIUM_MS: 5_000,
  MEDIUM_UNTIL_MS: 3 * 60_000,
  /** Then every 15 s until the bound. */
  SLOW_MS: 15_000,
  /** "Calculating…" becomes "isn't ready yet" (still polling). */
  SOFT_COPY_MS: 15_000,
  /** Polling stops; the page asks for a reload. */
  MAX_MS: 15 * 60_000,
} as const;

/** The score is in, or nothing we poll for can change the answer. */
export function isResultSettled(data: CandidateResult | undefined): boolean {
  if (!data) return false;
  if (data.show_score !== true) return true;
  // EF-10: a recruiter still owes a score — no amount of polling will
  // produce the total on its own.
  if (data.awaiting_review === true) return true;
  if (data.total_score === null) return false;
  // A total with a chip still spinning would be a lie on screen: keep going
  // (bounded by MAX_MS) until every machine-graded answer has its score.
  const perQuestion = data.per_question ?? [];
  return perQuestion.every((q) => q.pending_review === true || q.score !== null);
}

/**
 * The delay until the next tick, or `false` to stop. Pure, so a test can
 * walk the schedule without a clock. Ignores whether the last tick failed.
 */
export function nextPollDelay(
  data: CandidateResult | undefined,
  startedAt: number | null,
  now: number,
): number | false {
  if (startedAt === null) return false;
  if (isResultSettled(data)) return false;
  const elapsed = now - startedAt;
  if (elapsed >= RESULT_POLL.MAX_MS) return false;
  if (elapsed < RESULT_POLL.FAST_UNTIL_MS) return RESULT_POLL.FAST_MS;
  if (elapsed < RESULT_POLL.MEDIUM_UNTIL_MS) return RESULT_POLL.MEDIUM_MS;
  return RESULT_POLL.SLOW_MS;
}

export type ResultPollPhase =
  /** Not started (no attempt yet) or the result is settled. */
  | "idle"
  /** First seconds: "Calculating…". */
  | "calculating"
  /** Past the soft copy threshold, still polling: "isn't ready yet". */
  | "waiting"
  /** Past MAX_MS with no score: polling stopped, ask for a reload. */
  | "gave_up";

export function pollPhase(
  data: CandidateResult | undefined,
  startedAt: number | null,
  now: number,
): ResultPollPhase {
  if (startedAt === null || isResultSettled(data)) return "idle";
  const elapsed = now - startedAt;
  if (elapsed >= RESULT_POLL.MAX_MS) return "gave_up";
  if (elapsed >= RESULT_POLL.SOFT_COPY_MS) return "waiting";
  return "calculating";
}

export interface UseResultPollArgs {
  token: string;
  attemptId: string | null;
  /** The submit succeeded — only then is there a result to poll. */
  enabled: boolean;
  /** `Date.now()` when the submit succeeded; null until then. */
  startedAt: number | null;
}

export function useResultPoll({
  token,
  attemptId,
  enabled,
  startedAt,
}: UseResultPollArgs) {
  const active = enabled && attemptId !== null && startedAt !== null;

  const query = useQuery({
    queryKey: queryKeys.candidateResult(token, attemptId),
    queryFn: () => fetchCandidateResult(token, attemptId!),
    enabled: active,
    // Best-effort; swallow into the calculating/pending UI rather than
    // toasting or breaking the thanks copy. The interval is the retry.
    retry: false,
    refetchInterval: (q) => nextPollDelay(q.state.data, startedAt, Date.now()),
    refetchIntervalInBackground: false,
  });

  const data = query.data;
  const settled = isResultSettled(data);

  // The phase is a function of the clock, so something has to re-render when
  // a threshold passes — a fetch that lands BEFORE it would otherwise be the
  // last paint and the copy would stick (the pre-OPS-24 bug, once over).
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!active || settled || startedAt === null) return;
    const timers: ReturnType<typeof setTimeout>[] = [];
    for (const at of [RESULT_POLL.SOFT_COPY_MS, RESULT_POLL.MAX_MS]) {
      const remaining = startedAt + at - Date.now();
      if (remaining > 0) {
        timers.push(setTimeout(() => setTick((n) => n + 1), remaining));
      }
    }
    return () => timers.forEach(clearTimeout);
  }, [active, settled, startedAt]);

  // Back from the background → one immediate refetch while still unsettled
  // and inside the bound. Guarded by `document` for the test runner.
  const refetch = query.refetch;
  useEffect(() => {
    if (!active || settled || typeof document === "undefined") return;
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (nextPollDelay(undefined, startedAt, Date.now()) === false) return;
      void refetch();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [active, settled, startedAt, refetch]);

  const phase: ResultPollPhase = active
    ? pollPhase(data, startedAt, Date.now())
    : "idle";

  return { query, phase, settled };
}
